import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getWalletHistory } from "../api/endpoints/wallet/wallet";
import type { GetWalletHistory200 } from "../api/models/getWalletHistory200";
import type { GetWalletHistoryParams } from "../api/models/getWalletHistoryParams";
import type { GetWalletHistoryProduct } from "../api/models/getWalletHistoryProduct";
import type { WalletHistoryDetail } from "../api/models/walletHistoryDetail";
import { getBearerToken } from "../lib/authClient";

import "./WalletHistoryPage.css";

const authHeaders = () => {
  const token = getBearerToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
};

const PRODUCT_LABELS: Record<string, string> = {
  ON_RAMP: "On-Ramp",
  OFF_RAMP: "Off-Ramp",
  TRADE_SPOT: "Spot",
  TRADE_SWAP: "Swap",
  TRADE_LINEAR: "Futuros",
  DEPOSIT_ONCHAIN: "Depósito",
  WITHDRAW_ONCHAIN: "Retirada",
};

const ORDER_TYPE_LABELS: Record<string, string> = {
  MARKET: "Mercado",
  LIMIT: "Limite",
};

/**
 * Para ordens de trading (Spot/Futuros), extrai o tipo de execução
 * (Mercado/Limite) de `details.orderType`. Retorna null para os demais produtos.
 */
const getOrderTypeLabel = (item: WalletHistoryDetail): string | null => {
  if (item.product !== "TRADE_SPOT" && item.product !== "TRADE_LINEAR") return null;
  const details = item.details as { orderType?: string; limitPrice?: string } | null;
  const orderType = details?.orderType;
  if (!orderType) return null;
  const label = ORDER_TYPE_LABELS[orderType] ?? orderType;
  if (orderType === "LIMIT" && details?.limitPrice) {
    return `${label} @ ${details.limitPrice}`;
  }
  return label;
};

const SIDE_LABELS: Record<string, string> = {
  BUY: "Compra",
  SELL: "Venda",
  DEPOSIT: "Depósito",
  WITHDRAW: "Retirada",
};

const STATUS_LABELS: Record<string, string> = {
  CREATED: "Criada",
  AWAITING_PAYMENT: "Aguardando",
  PROCESSING: "Processando",
  CONFIRMED: "Confirmada",
  FAILED: "Falhou",
  EXPIRED: "Expirada",
  CANCELLED: "Cancelada",
};

const PERIOD_OPTIONS = [
  { value: "7d", label: "7 dias" },
  { value: "30d", label: "30 dias" },
  { value: "90d", label: "90 dias" },
  { value: "all", label: "Tudo" },
];

const PRODUCT_OPTIONS: Array<{ value: GetWalletHistoryProduct | ""; label: string }> = [
  { value: "", label: "Todos" },
  { value: "TRADE_SWAP", label: "Swap" },
  { value: "TRADE_SPOT", label: "Spot" },
  { value: "TRADE_LINEAR", label: "Futuros" },
  { value: "ON_RAMP", label: "On-Ramp" },
  { value: "OFF_RAMP", label: "Off-Ramp" },
  { value: "DEPOSIT_ONCHAIN", label: "Depósito" },
  { value: "WITHDRAW_ONCHAIN", label: "Retirada" },
];

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
};

export default function WalletHistoryPage() {
  const navigate = useNavigate();

  const [product, setProduct] = useState<GetWalletHistoryProduct | "">("");
  const [period, setPeriod] = useState("7d");

  const [items, setItems] = useState<WalletHistoryDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchHistory = useCallback(
    async (opts?: { cursor?: string }) => {
      const isLoadMore = !!opts?.cursor;
      if (isLoadMore) setLoadingMore(true);
      else {
        setLoading(true);
        setError(null);
      }

      try {
        const params: GetWalletHistoryParams = { period, limit: 20 };
        if (product) params.product = product;
        if (opts?.cursor) params.cursor = opts.cursor;

        const response = await getWalletHistory(params, authHeaders());
        if (response.status === 200 && "data" in response.data) {
          const body = response.data as GetWalletHistory200;
          const newItems = body.data ?? [];
          setItems((prev) => (isLoadMore ? [...prev, ...newItems] : newItems));
          setNextCursor(body.pagination?.nextCursor ?? null);
          setHasMore(body.pagination?.hasMoreAfter ?? false);
        } else {
          const msg =
            (response.data as { message?: string })?.message ??
            `Falha ao buscar histórico (status ${response.status}).`;
          if (!isLoadMore) setError(msg);
        }
      } catch (err) {
        console.error("Erro ao buscar histórico:", err);
        if (!isLoadMore)
          setError(err instanceof Error ? err.message : "Erro desconhecido");
      } finally {
        if (isLoadMore) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [product, period],
  );

  useEffect(() => {
    setItems([]);
    setNextCursor(null);
    fetchHistory();
  }, [fetchHistory]);

  return (
    <div className="page-centered">
      <div className="walletHistory-wrap">
        <div className="walletHistory-header">
          <button
            type="button"
            className="walletHistory-back"
            onClick={() => navigate("/portfolio/ativos")}
          >
            ← Voltar
          </button>
          <h1 className="walletHistory-title">Histórico</h1>
        </div>

        {/* Filtro de produto */}
        <div className="walletHistory-chips">
          {PRODUCT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`walletHistory-chip${product === opt.value ? " walletHistory-chip--active" : ""}`}
              onClick={() => setProduct(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Filtro de período */}
        <div className="walletHistory-chips walletHistory-chips--period">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`walletHistory-chip${period === opt.value ? " walletHistory-chip--active" : ""}`}
              onClick={() => setPeriod(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="walletHistory-status">Carregando...</div>
        )}

        {error && !loading && (
          <div className="walletHistory-status walletHistory-status--error">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="walletHistory-status">Nenhum registro encontrado.</div>
        )}

        {items.length > 0 && (
          <div className="walletHistory-list">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className="walletHistory-item"
                onClick={() => navigate(`/wallet/history/${item.id}`)}
              >
                <div className="walletHistory-item-left">
                  <div className="walletHistory-item-product">
                    {PRODUCT_LABELS[item.product] ?? item.product}
                    {getOrderTypeLabel(item) && (
                      <span className="walletHistory-item-ordertype">
                        {getOrderTypeLabel(item)}
                      </span>
                    )}
                    <span
                      className={`walletHistory-item-side walletHistory-item-side--${item.side.toLowerCase()}`}
                    >
                      {SIDE_LABELS[item.side] ?? item.side}
                    </span>
                  </div>
                  <div className="walletHistory-item-date">
                    {formatDate(item.createdAt)}
                  </div>
                </div>
                <div className="walletHistory-item-right">
                  <div className="walletHistory-item-amount">
                    {item.amount} {item.coin}
                  </div>
                  <div
                    className={`walletHistory-item-status walletHistory-item-status--${item.status.toLowerCase()}`}
                  >
                    {STATUS_LABELS[item.status] ?? item.status}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {hasMore && (
          <button
            type="button"
            className="walletHistory-loadMore"
            onClick={() => nextCursor && fetchHistory({ cursor: nextCursor })}
            disabled={loadingMore}
          >
            {loadingMore ? "Carregando..." : "Carregar mais"}
          </button>
        )}
      </div>
    </div>
  );
}
